import React from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import {
    CcRequirementReviewMethod,
    CcRequirementSmartCompliance,
} from "server/models"
import { EDIT } from "const"
import {
    StoreType,
    CcRequirementObjectProps,
    addModal,
    createFileName,
    setDriveOutsideMode,
} from "client/store"
import {
    FormGroup,
    Label,
    Input,
    Textarea,
    Button,
    DivInput,
    Link,
    Empty,
    CheckboxWrapper,
    CheckButton,
} from "client/components"

import FileEdit from "client/pages/drive/fileEdit"
import { RequirementForm } from "./styled"

const Form = ({
    requirement,
    editAccess,
}: CcRequirementObjectProps) => {
    const baseRoute = useSelector((state: StoreType) => state.ccRequirement.baseRoute)
    const typeList = useSelector((state: StoreType) => state.ccRequirementType.typeList)
    const priorityList = useSelector((state: StoreType) => state.ccRequirementPriority.priorityList)
    const statusList = useSelector((state: StoreType) => state.ccRequirementStatus.statusList)
    const methodList = useSelector((state: StoreType) => state.ccRequirementReviewMethod.methodList)
    const methodIds = useSelector((state: StoreType) => state.ccRequirementReviewMethod.methodIds)
    const complianceList = useSelector((state: StoreType) => state.ccRequirementSmartCompliance.complianceList)
    const complianceIds = useSelector((state: StoreType) => state.ccRequirementSmartCompliance.complianceIds)

    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const editUrl = `${baseRoute}/${EDIT}/${requirement.id}`

    const {
        file,
        typeId,
        priorityId,
        statusId,
        reviewMethodsParsed,
        smartComplianceParsed,
    } = requirement

    const columnNumber = 2

    const methodColumnLength = Math.ceil(methodIds.length / columnNumber)
    const methodColumns: { [columnIndex: number]: CcRequirementReviewMethod[] } = {}
    for (let i = 0; i < columnNumber; i++) {
        const column = []
        const start = i * methodColumnLength
        const end = start + methodColumnLength
        for (let k = start; k < end; k++) {
            const methodId = methodIds[k]
            column.push(methodList[methodId])
        }
        methodColumns[i] = column
    }

    const complianceColumnLength = Math.ceil(complianceIds.length / columnNumber)
    const complianceColumns: { [columnIndex: number]: CcRequirementSmartCompliance[] } = {}
    for (let i = 0; i < columnNumber; i++) {
        const column = []
        const start = i * complianceColumnLength
        const end = start + complianceColumnLength
        for (let k = start; k < end; k++) {
            const complianceId = complianceIds[k]
            column.push(complianceList[complianceId])
        }
        complianceColumns[i] = column
    }

    return (
        <RequirementForm>
            <Row>
                <Col xs={12} md={4}>
                    <FormGroup>
                        <Label>{lang.uniqueId}</Label>
                        <Input type="text" value={requirement.uniqueId} readOnly />
                    </FormGroup>
                </Col>
                <Col xs={12} md={8}>
                    <FormGroup>
                        <Label>{lang.name}</Label>
                        <Input type="text" value={requirement.name} readOnly />
                    </FormGroup>
                </Col>
            </Row>
            <Row>
                <Col xs={12}>
                    <FormGroup>
                        <Label>{lang.description}</Label>
                        <Textarea value={requirement.description} readOnly />
                    </FormGroup>

                    <FormGroup>
                        <Label>{lang.file}</Label>
                        <DivInput widePadding>
                            {
                                file ? (
                                    <Link
                                        href="/#"
                                        onClick={() => {
                                            dispatch(setDriveOutsideMode(true))
                                            dispatch(addModal({
                                                key: "viewDocument",
                                                content: <FileEdit fileId={file.id} />,
                                                onClose: () => dispatch(setDriveOutsideMode(false)),
                                            }))
                                        }}
                                    >
                                        {createFileName(file)}
                                    </Link>
                                ) : <Empty />
                            }
                        </DivInput>
                    </FormGroup>

                    <FormGroup>
                        <Label>{lang.type}</Label>
                        <Input type="text" value={typeList[typeId] ? typeList[typeId].name : ""} readOnly />
                    </FormGroup>

                    <FormGroup>
                        <Label>{lang.priority}</Label>
                        <Input type="text" value={priorityList[priorityId] ? priorityList[priorityId].name : ""} readOnly />
                    </FormGroup>

                    <FormGroup>
                        <Label>{lang.status}</Label>
                        <Input type="text" value={statusList[statusId] ? statusList[statusId].name : ""} readOnly />
                    </FormGroup>

                    <FormGroup>
                        <Label>{lang.reviewMethods}</Label>
                        <CheckboxWrapper>
                            {
                                Object.keys(methodColumns).map((index) => (
                                    <Col key={index} xs={12} md={12 / columnNumber}>
                                        {
                                            methodColumns[Number(index)].map((method) => method && (
                                                <CheckButton
                                                    key={method.id}
                                                    checked={reviewMethodsParsed && reviewMethodsParsed[method.id]}
                                                    title={method.name}
                                                />
                                            ))
                                        }
                                    </Col>
                                ))
                            }
                        </CheckboxWrapper>
                    </FormGroup>

                    <FormGroup>
                        <Label>{lang.smartCompliance}</Label>
                        <CheckboxWrapper>
                            {
                                Object.keys(complianceColumns).map((index) => (
                                    <Col key={index} xs={12} md={12 / columnNumber}>
                                        {
                                            complianceColumns[Number(index)].map((compliance) => compliance && (
                                                <CheckButton
                                                    key={compliance.id}
                                                    checked={smartComplianceParsed && smartComplianceParsed[compliance.id]}
                                                    title={compliance.name}
                                                />
                                            ))
                                        }
                                    </Col>
                                ))
                            }
                        </CheckboxWrapper>
                    </FormGroup>
                </Col>
                <Col xs={12}>
                    {
                        editAccess && (
                            <Button
                                row
                                onClick={() => dispatch(addModal({ url: editUrl }))}
                            >
                                {lang.edit}
                            </Button>
                        )
                    }
                </Col>
            </Row>
        </RequirementForm>
    )
}
export default Form
